import React, { useState } from 'react';
import { xpToLevel, COSMETICS_CONFIG, BADGES_CONFIG } from '../gamificationConfig';

// ── Shared icon ──────────────────────────────────────────────────────────────

function LockIcon({ size = 10 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true">
      <rect x="3" y="8" width="10" height="7" rx="1.5" />
      <path d="M5 8V5.5a3 3 0 0 1 6 0V8" />
    </svg>
  );
}

// ── Tab: Stats ───────────────────────────────────────────────────────────────

function StatsTab({ stats }) {
  const items = [
    { label: 'Clean Runs',          value: stats.cleanRuns,                  emoji: '✅' },
    { label: 'Exercises Completed', value: stats.totalExercises,             emoji: '📚' },
    { label: 'Current Streak',      value: `${stats.currentStreak} days`,    emoji: '🔥' },
    { label: 'Max Streak',          value: `${stats.maxStreak ?? 0} days`,   emoji: '🏆' },
  ];
  return (
    <div className="locker-stats-grid">
      {items.map(item => (
        <div key={item.label} className="locker-stat-tile">
          <span className="locker-stat-emoji" aria-hidden="true">{item.emoji}</span>
          <span className="locker-stat-value ib-mono">{item.value}</span>
          <span className="locker-stat-label">{item.label}</span>
        </div>
      ))}
    </div>
  );
}

// ── Tab: Badges ──────────────────────────────────────────────────────────────

function BadgesTab({ earnedBadgeIds }) {
  return (
    <div className="locker-badges-grid">
      {BADGES_CONFIG.map(badge => {
        const earned = earnedBadgeIds.includes(badge.id);
        return (
          <div
            key={badge.id}
            className={`locker-badge-tile ${earned ? 'locker-badge-earned' : 'locker-badge-locked'}`}
            title={badge.description}
          >
            <span className="locker-badge-emoji" aria-hidden="true">{badge.emoji}</span>
            <span className="locker-badge-name">{badge.label}</span>
            <span className="locker-badge-desc">{badge.description}</span>
            {!earned && <span className="locker-badge-lock-overlay"><LockIcon size={12} /></span>}
          </div>
        );
      })}
    </div>
  );
}

// ── Tab: Cosmetics ───────────────────────────────────────────────────────────

function RingPreview({ ringId, cssColor }) {
  if (!cssColor) {
    return (
      <span className="locker-ring-preview locker-ring-none" aria-hidden="true">∅</span>
    );
  }
  return (
    <span
      className="locker-ring-preview"
      data-ring-id={ringId}
      style={{ background: cssColor }}
      aria-hidden="true"
    />
  );
}

function CosmeticsTab({ playerData, userLevel, onEquipCosmetic }) {
  const sections = [
    {
      key:        'avatars',
      apiType:    'avatar',
      label:      'Avatars',
      items:      COSMETICS_CONFIG.avatars,
      equippedId: playerData.equippedAvatarId,
      renderIcon: (item) => <span className="locker-cosmetic-emoji" aria-hidden="true">{item.emoji}</span>,
    },
    {
      key:        'rings',
      apiType:    'ring',
      label:      'Rings',
      items:      COSMETICS_CONFIG.rings,
      equippedId: playerData.equippedRingId,
      renderIcon: (item) => <RingPreview ringId={item.id} cssColor={item.cssColor} />,
    },
    {
      key:        'pets',
      apiType:    'pet',
      label:      'Pets',
      items:      COSMETICS_CONFIG.pets,
      equippedId: playerData.equippedPetId,
      renderIcon: (item) => <span className="locker-cosmetic-emoji" aria-hidden="true">{item.emoji}</span>,
    },
  ];

  return (
    <div className="locker-cosmetics">
      {sections.map(section => (
        <div key={section.key} className="locker-cosmetic-section">
          <div className="locker-cosmetic-section-title">{section.label}</div>
          <div className="locker-cosmetic-grid">
            {section.items.map(item => {
              const unlocked = userLevel >= item.unlockLevel;
              const equipped = item.id === section.equippedId;
              return (
                <div
                  key={item.id}
                  role={unlocked && !equipped ? 'button' : undefined}
                  tabIndex={unlocked && !equipped ? 0 : undefined}
                  className={[
                    'locker-cosmetic-item',
                    unlocked ? 'locker-cosmetic-unlocked' : 'locker-cosmetic-locked',
                    equipped ? 'locker-cosmetic-equipped' : '',
                  ].filter(Boolean).join(' ')}
                  title={
                    !unlocked
                      ? `Unlocks at Level ${item.unlockLevel}`
                      : equipped ? 'Equipped' : 'Click to equip'
                  }
                  onClick={() => {
                    if (unlocked && !equipped && onEquipCosmetic) {
                      onEquipCosmetic(section.apiType, item.id);
                    }
                  }}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && unlocked && !equipped && onEquipCosmetic) {
                      onEquipCosmetic(section.apiType, item.id);
                    }
                  }}
                  style={unlocked && !equipped ? { cursor: 'pointer' } : undefined}
                >
                  {section.renderIcon(item)}
                  <span className="locker-cosmetic-label">{item.label}</span>

                  {!unlocked && (
                    <span className="locker-cosmetic-lock">
                      <LockIcon size={9} />
                      <span>Lvl {item.unlockLevel}</span>
                    </span>
                  )}
                  {equipped && unlocked && (
                    <span className="locker-cosmetic-on-badge">On</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── TheLocker ────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'stats',     label: 'Stats'     },
  { id: 'badges',    label: 'Badges'    },
  { id: 'cosmetics', label: 'Cosmetics' },
];

export default function TheLocker({ playerData, onEquipCosmetic }) {
  const [activeTab, setActiveTab] = useState('stats');
  // Use globalXP (max across all language tracks) for cosmetic unlock level —
  // items unlocked via Python XP remain available when the Java track is active.
  const userLevel = xpToLevel(playerData.globalXP ?? playerData.lifetimeXP);

  return (
    <div className="locker-card cd-card">

      {/* Header */}
      <div className="cd-card-header">
        <span className="cd-card-title">The Locker</span>
        <span className="locker-level-badge ib-mono">Lvl {userLevel}</span>
      </div>

      {/* Tab bar */}
      <div className="locker-tabs" role="tablist">
        {TABS.map(tab => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            className={`locker-tab ${activeTab === tab.id ? 'locker-tab-active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="locker-body">
        {activeTab === 'stats'     && <StatsTab     stats={playerData.stats} />}
        {activeTab === 'badges'    && <BadgesTab     earnedBadgeIds={playerData.earnedBadgeIds} />}
        {activeTab === 'cosmetics' && <CosmeticsTab  playerData={playerData} userLevel={userLevel} onEquipCosmetic={onEquipCosmetic} />}
      </div>

    </div>
  );
}
